import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../firebase/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import './TaskSearch.css';
import { globalSearch, SearchResult } from "../../../services/searchServices/GlobalSearchService";
import headerserachicon from "../../../assets/images/headerserachicon.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faUsers, faTasks } from "@fortawesome/free-solid-svg-icons";
import { TasksIcon } from "../../components/SvgIcons/TasksIcon";
import { ParticipantsSVGIcon } from "../../components/SvgIcons/ParticipantsSVGIcons";
import { TeamsSvgIcon } from "../../components/SvgIcons/TeamsIcon";

const GlobalSearch: React.FC = () => {
    const { currentUser, currentOrganization } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    const [toggleSearch, setToggleSearch] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [filteredResults, setFilteredResults] = useState<SearchResult[]>([]);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const displayedResults = filteredResults.slice(0, 10);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredResults([]);
            return;
        }

        const debounceTimer = setTimeout(async () => {
            if (currentUser) {
                setIsSearching(true);
                try {
                    const authToken = await currentUser.getIdToken();
                    const results = await globalSearch(authToken, searchTerm);
                    setFilteredResults(results);
                } catch (error) {
                    console.error("Failed to search:", error);
                    setFilteredResults([]);
                } finally {
                    setIsSearching(false);
                }
            }
        }, 400);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, currentUser]);

    useEffect(() => {
        setHoveredIndex(displayedResults.length > 0 ? 0 : -1);
    }, [filteredResults]); // Keep dependency on filteredResults so it resets when data changes

    const handleNavigation = (result: SearchResult) => {
        setToggleSearch(false);
        setSearchTerm("");
        setHoveredIndex(-1);
        if (result.type === 'task') {
            navigate(`/task/${currentOrganization?.abbreviation}-${result.friendlyId}`);
        } else if (result.type === 'participant') {
            navigate(`/edit/participant/${result.id}/service-overview`);
        } else if (result.type === 'employee') {
            navigate(`/profile/user/${result.id}`);
        }
    };

    useEffect(() => {
        setToggleSearch(false);
        setSearchTerm("");
    }, [location]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // 3. Check if click is outside Search Bar AND outside Results Dropdown
            const clickedInsideSearch = searchRef.current && searchRef.current.contains(event.target as Node);
            const clickedInsideResults = resultsRef.current && resultsRef.current.contains(event.target as Node);

            if (!clickedInsideSearch && !clickedInsideResults) {
                setToggleSearch(false);
                setSearchTerm("");
                setHoveredIndex(-1);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Use displayedResults for navigation limits
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHoveredIndex((prevIndex) =>
                prevIndex < displayedResults.length - 1 ? prevIndex + 1 : 0
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHoveredIndex((prevIndex) =>
                prevIndex > 0 ? prevIndex - 1 : displayedResults.length - 1
            );
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (hoveredIndex !== -1 && displayedResults[hoveredIndex]) {
                handleNavigation(displayedResults[hoveredIndex]);
            }
        }
    };

    const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const getHighlightedText = (text: string, highlight: string) => {
        if (!highlight.trim()) {
            return <span>{text}</span>;
        }

        const safeHighlight = escapeRegExp(highlight);
        const parts = text.split(new RegExp(`(${safeHighlight})`, 'gi'));
        return (
            <span>
                {parts.map((part, i) =>
                    part.toLowerCase() === highlight.toLowerCase() ? (
                        <span key={i} style={{ backgroundColor: 'rgba(126, 87, 194, 0.3)' }}>
                            {part}
                        </span>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    };

    const renderResult = (result: SearchResult, index: number) => {
        let IconDisplay: React.ReactNode;
        let title = '';
        let description = '';

        switch (result.type) {
            case 'task':
                IconDisplay = <TasksIcon className="mr-3 text-gray-500 w-7 h-7" />;
                title = `[${currentOrganization?.abbreviation}-${result.friendlyId}] ${result.title}`;
                description = `Participant: ${result.participantName}`;
                break;
            case 'participant':
                IconDisplay = <ParticipantsSVGIcon className="mr-3 text-gray-500 w-7 h-7" />;
                title = `${result.firstName} ${result.lastName}`;
                description = `Region ${result.region} - ${result.waiver}`;
                break;
            case 'employee':
                IconDisplay = <TeamsSvgIcon className="mr-3 text-gray-500 w-7 h-7" />;
                title = `${result.firstName} ${result.lastName}`;
                description = result.email;
                break;
        }

        return (
            <div
                key={`${result.type}-${result.id}`}
                className={`header_serach_task_card p-3 border-b border-gray-200 cursor-pointer ${index === hoveredIndex ? 'bg-gray-100 hovered-option' : 'bg-white'}`}
                // onClick={() => handleNavigation(result)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    handleNavigation(result);
                }}
                onMouseEnter={() => setHoveredIndex(index)}
            >
                <div className="flex items-center">
                    {IconDisplay}
                    <div>
                        <h3
                            className="font-bold text-sm text-gray-800 truncate"
                            style={{ maxWidth: '350px' }}
                            title={title}
                            aria-label={title}
                        >
                            {getHighlightedText(title, searchTerm)}
                        </h3>
                        <p className="text-xs text-gray-600 truncate">{description}</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div ref={searchRef}>
            <div className="header_search_bar" >
                <div className="custom_input_flled !w-full  mb-0">
                    <input
                        ref={inputRef}
                        className="relative filled_white"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            if (!toggleSearch) setToggleSearch(true);
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Search here..."
                        type="search"
                    />
                    <div
                        className="cursor-pointer search_icon"
                        onClick={() => {
                            setToggleSearch(!toggleSearch);
                            if (!toggleSearch) {
                                inputRef.current?.focus();
                            }
                        }}
                    >
                        <img src={headerserachicon} alt="search" />
                    </div>
                </div>
            </div>

            {toggleSearch && searchTerm.trim() !== "" && (
                // 4. Attach resultsRef here. 
                // This allows the layout to remain exactly as it was originally.
                <div className="header_serach_task mt-2" ref={resultsRef}>
                    {isSearching ? (
                        <div className="p-2 text-gray-500 text-xs text-center">Searching...</div>
                    ) : (
                        <>
                            {displayedResults.map((result, index) => renderResult(result, index))}

                            {searchTerm.trim() !== "" && !isSearching && filteredResults.some(r => r.type === 'task') && (
                                <div
                                    className="p-3 text-center text-sm font-medium text-[#7E57C2] hover:bg-gray-50 cursor-pointer border-t border-gray-100 transition-colors"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setToggleSearch(false);
                                        setSearchTerm("");
                                        navigate(`/tasks?searchTerm=${searchTerm.trim()}`);
                                    }}
                                >
                                    View all task results
                                </div>
                            )}
                            {searchTerm.trim() !== "" && !isSearching && filteredResults.length === 0 && (
                                <div className="p-2 text-gray-500 text-xs text-center">No results found</div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalSearch;
